class ScRelatedRecipes extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.cacheDom();
    if (!this.grid || !this.template) return;

    this.recipes = this.parseRecipes();
    if (!this.recipes.length) return;

    this.renderRecipes();
    this.refreshSlider();
  }

  cacheDom() {
    this.slider = this.querySelector("css-slider");
    this.grid = this.querySelector("[data-sc-related-recipes-grid]");
    this.template = this.querySelector("[data-sc-related-recipes-template]");
  }

  parseRecipes() {
    const dataNode = this.querySelector("[data-sc-related-recipes-json]");
    const raw = dataNode?.value ?? dataNode?.textContent ?? "[]";

    try {
      const parsed = JSON.parse(raw.trim());
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("sc-related-recipes: invalid recipe data", error);
      return [];
    }
  }

  renderRecipes() {
    const fragment = document.createDocumentFragment();

    this.recipes.forEach((recipe, index) => {
      fragment.appendChild(this.createRecipeNode(recipe, index + 1));
    });

    this.grid.appendChild(fragment);
  }

  refreshSlider() {
    if (!this.slider) return;

    const apply = () => {
      if (!this.slider?.sliderEnabled) return;

      this.grid
        .querySelectorAll(".slider-item:not(.css-slide)")
        .forEach((element) => {
          element.classList.add("css-slide");
        });

      this.slider.afterAppend();
      this.slider.resetSlider(true);
    };

    if (this.slider.sliderEnabled) {
      apply();
      return;
    }

    this.slider.addEventListener("ready", () => apply(), { once: true });
  }

  createRecipeNode(recipe, index) {
    const slide = document.createElement("div");
    slide.className = "slider-item css-slide";

    const node = this.template.content.firstElementChild.cloneNode(true);

    node.querySelectorAll("[data-sc-field]").forEach((element) => {
      const field = element.dataset.scField;

      if (field === "url") {
        element.setAttribute("href", recipe.url || "#");
        element.setAttribute("title", recipe.title || "");
      }

      if (field === "title") {
        element.textContent = recipe.title || "";
      }

      if (field === "excerpt") {
        element.textContent = recipe.excerpt || "";
      }

      if (field === "author") {
        element.textContent = recipe.author || "";
        element.hidden = !recipe.author;
      }

      if (field === "date") {
        element.textContent = this.formatDate(recipe.published_at);
        element.hidden = !recipe.published_at;
      }

      if (field === "image-wrap") {
        element.hidden = !recipe.image;
      }

      if (field === "image" && recipe.image) {
        const figure = element.closest('[data-sc-field="image-wrap"]');
        element.setAttribute("src", this.getCroppedImageUrl(recipe.image));
        element.setAttribute("alt", recipe.title || "");
        element.setAttribute("loading", "lazy");
        element.setAttribute("decoding", "async");
        element.removeAttribute("hidden");
        element.addEventListener(
          "load",
          () => figure?.classList.add("lazyloaded"),
          { once: true },
        );
      }
    });

    if (this.dataset.emphasizeFirst === "true" && index === 1) {
      node.classList.add("blog-item--emphasized");
    }

    slide.appendChild(node);
    return slide;
  }

  getCroppedImageUrl(url) {
    if (!url || !url.includes("cdn.shopify.com")) return url;

    const ratio = Number(this.dataset.imageAspectRatio || 1.33333);
    const width = 800;
    const height = Math.round(width / ratio);
    const joiner = url.includes("?") ? "&" : "?";

    return `${url}${joiner}width=${width}&height=${height}&crop=center`;
  }

  formatDate(value) {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat(document.documentElement.lang || "de", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  }
}

if (!customElements.get("sc-related-recipes")) {
  customElements.define("sc-related-recipes", ScRelatedRecipes);
}
